import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { intakeFormUpdateFieldValue } from '~/actions/intakeForm';
import { IconType } from '~/eds/src/types/IconType';
import { StatusType } from '~/eds/src/types/StatusType';
import { TicketStageType } from '~/enums/TicketStageType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import * as selectors from '~/redux/selectors';
import { FormQuestionFieldState } from '~/redux/selectors';

export const useGetCommonQuestionFieldProps = ({
  question,
  search,
}: {
  question: FormQuestionFieldState;
  search?: string;
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const isFormValidated = useSelector(selectors.selectFormValidation);
  const ticketStage = useSelector(selectors.selectTicketStage);
  const isIntakeFormFieldWithReadOnlyEnabled = useFlag(
    FlagType.IntakeFormFieldWithReadOnly,
  );
  const isWSSIntegrationPhase2M1M3Enabled = useFlag(
    FlagType.WSSIntegrationPhase2M1M3,
  );
  const {
    title,
    description,
    isReadOnly,
    isRequired,
    field: {
      errorMessage,
      id: fieldId,
      isPlaceHolder,
      warningMessage,
      value,
      wellKnownKey,
    },
  } = question;

  const shouldShowDatabaseIcon =
    isReadOnly &&
    isIntakeFormFieldWithReadOnlyEnabled &&
    !(isWSSIntegrationPhase2M1M3Enabled && wellKnownKey);

  const actions = shouldShowDatabaseIcon
    ? [
        {
          icon: 'database' as IconType,
          mode: 'icon' as const,
          text: intl.formatMessage({
            id: 'useGetCommonQuestionFieldProps.syncedFieldTooltip',
            defaultMessage:
              'This is a synced field and cannot be edited in Evisort',
            description:
              'Displays in a tooltip on the intake form when a question field is read-only',
          }),
        },
      ]
    : [];
  const showError = !!(isFormValidated && errorMessage);
  const isDisabled =
    isPlaceHolder &&
    [TicketStageType.Sign, TicketStageType.Finalize].includes(ticketStage);

  const updateField = useCallback(
    (newValue: any, key = 'value') => {
      return dispatch(
        intakeFormUpdateFieldValue({
          id: fieldId,
          value: { ...value, [key]: newValue },
        }),
      );
    },
    [dispatch, fieldId, value],
  );

  return {
    id: `intakeform-question-${fieldId}`,
    name: `intakeform-question-${fieldId}`,
    label: title,
    description: description ?? undefined,
    search,
    required: isRequired,
    actions,
    actionsSize: 'xs' as const,
    disabled: isDisabled || isReadOnly,
    error: showError ? errorMessage : undefined,
    statusMessage:
      !!ticketStage && warningMessage
        ? {
            status: 'warning' as StatusType,
            message: warningMessage,
          }
        : undefined,
    onChange: updateField,
  };
};
